import { useEffect, useState } from "react";

import { api, ApiError } from "../api/client";
import { formatDateTime } from "../lib/format";
import { decodeBackend, detectionBackend, facesBackend } from "../lib/runtime";
import type { Backend } from "../lib/runtime";
import { ui } from "../lib/ui";
import { useAsync } from "../lib/useAsync";

const POLL_MS = 2000;

/** What the worker PC runs on, before a run is queued: person detection
 *  (torch/OpenVINO device), InsightFace's execution provider and the video
 *  decoder — each as the worker last proved it (GET /runs/runtime). The
 *  detector and demographics choices the operator is about to queue with
 *  are reflected, so the strip describes *this* run. */
export function WorkerRuntimePanel({
  model,
  faces,
}: {
  /** Detector id the run will use; "" = the machine default. */
  model: string;
  /** The Demographics switch. */
  faces: boolean;
}) {
  const runtime = useAsync(() => api.workerRuntime(), []);
  const [checkError, setCheckError] = useState<string | null>(null);
  const waiting = !!runtime.data?.requested_at;

  // While a re-check is pending the worker answers when idle: poll for it.
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(runtime.reload, POLL_MS);
    return () => clearInterval(timer);
  }, [waiting, runtime.reload]);

  const checkAgain = async () => {
    setCheckError(null);
    try {
      await api.checkWorkerRuntime();
      runtime.reload();
    } catch (err) {
      setCheckError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const report = runtime.data?.report ?? null;
  return (
    <div className={`${ui.toolbar} mb-3 flex-col items-stretch! gap-y-2!`}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className={ui.label}>Worker runs on</span>
        {report && (
          <>
            <Item
              name="Detection"
              backend={detectionBackend(report, model || null)}
              title={`${report.device_label ?? report.device ?? ""}${
                report.detector_backend ? ` — ${report.detector_backend}` : ""
              }`}
            />
            <Item
              name="Demographics"
              backend={facesBackend(report, faces)}
              title={
                faces
                  ? `InsightFace on ${report.faces_backend ?? "?"} (installed providers: ${
                      report.faces_providers.join(", ") || "none"
                    })`
                  : "Off for this run: no gender/age, no face crops, no staff recognised"
              }
            />
            <Item
              name="Decode"
              backend={decodeBackend(report)}
              title={report.decoder_line ?? ""}
            />
          </>
        )}
        {!report && !runtime.loading && !runtime.error && (
          <span className="text-[13px] text-muted">
            No worker has reported yet — queued runs wait until the worker starts.
          </span>
        )}
        {runtime.error && (
          <span className="text-[13px] text-danger">Unavailable ({runtime.error})</span>
        )}
        <span className="ml-auto flex items-center gap-2 text-xs text-muted">
          {report && (
            <span title={report.device_label ?? undefined}>
              {report.host ? `${report.host} · ` : ""}checked{" "}
              {formatDateTime(report.reported_at)}
            </span>
          )}
          <button
            type="button"
            className={ui.btnQuiet}
            disabled={waiting}
            title="Have the worker probe its GPU, face backend and hardware decode again (when it is not busy with a run)"
            onClick={checkAgain}
          >
            {waiting ? "Waiting for the worker…" : "Check again"}
          </button>
        </span>
      </div>
      {report && report.warnings.length > 0 && (
        <ul className={`${ui.notice} ${ui.noticeError} mb-0! list-disc pl-7 whitespace-pre-wrap`}>
          {report.warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}
      {checkError && <p className={`${ui.notice} ${ui.noticeError} mb-0!`}>{checkError}</p>}
    </div>
  );
}

function Item({ name, backend, title }: { name: string; backend: Backend; title: string }) {
  // A GPU is green; the CPU is neutral (a CPU-only PC is not an error — the
  // warnings below say when a GPU is present but unused).
  const suffix = backend.gpu ? " · GPU" : "";
  return (
    <span className="flex items-center gap-2 text-[13px]" title={title}>
      <span className="text-muted">{name}</span>
      <span className={`${ui.pill} normal-case! ${backend.gpu ? ui.pillSucceeded : ""}`}>
        {backend.label}
        {suffix}
      </span>
    </span>
  );
}
